'use client';

import { Fragment, type ReactNode } from 'react';
import { Grid, Skeleton, Typography } from '@mui/material';

import { tss } from 'tss-react/mui';

import type { ImageLoader } from 'next/image';

import { or2ImageLoader } from '@/modules/image/loaders/or2ImageLoader';

import { Or2Container } from '../layout/Or2Container';
import { Or2Image } from '../image/Or2Image';
import { Or2Avatar } from '../Avatar/Or2Avatar';

export const OR2_PAGE_HEADER_AVATAR_SIZE = 50;

const useStyles = tss
  .withParams<{ standalone: boolean }>()
  .create(({ theme, standalone }) => {
    return {
      root: {
        paddingBottom: theme.spacing(2.5),
        paddingTop: theme.spacing(3),
        display: 'flex',
        backgroundColor: theme.palette.common.white,
        minHeight: '67px',
        width: '100%',
        borderLeft: `1px solid ${theme.cardBorder}`,
        borderRight: `1px solid ${theme.cardBorder}`,
        borderTopLeftRadius: theme.shape.borderRadius,
        // if standalone, add bottom left and right radius
        ...(standalone && {
          borderBottomLeftRadius: theme.shape.borderRadius,
          borderBottomRightRadius: theme.shape.borderRadius,
          borderBottom: `1px solid ${theme.cardBorder}`,
        }),
      },
      avatar: {
        width: `${OR2_PAGE_HEADER_AVATAR_SIZE}px`,
        height: `${OR2_PAGE_HEADER_AVATAR_SIZE}px`,
      },
      avatarWrapper: {
        paddingRight: theme.spacing(2),
      },
    };
  });

interface Props {
  className?: string;
  title: ReactNode;
  iconUrl?: string | null;
  iconNode?: ReactNode;
  subtitle?: ReactNode;
  hideSubtitle?: boolean;
  loading?: boolean;
  standalone?: boolean;
  imageLoader?: ImageLoader | undefined;
  actions?: ReactNode[];
}

export const Or2PageHeader = ({
  className,
  title,
  subtitle,
  hideSubtitle = false,
  iconUrl,
  iconNode,
  loading = false,
  standalone = false,
  imageLoader,
  actions,
}: Props) => {
  const { classes, cx } = useStyles({ standalone });

  return (
    <div className={cx(className, classes.root)}>
      <Or2Container mainContent={false} removePaddingOnSmallScreen={false}>
        <Grid container justifyContent={'space-between'} width={'100%'}>
          <Grid container size="grow">
            <Grid className={classes.avatarWrapper}>
              <Or2Avatar className={classes.avatar}>
                {iconUrl ? (
                  <Or2Image
                    src={iconUrl}
                    alt={title?.toString() || ''}
                    loading={loading}
                    loader={imageLoader || or2ImageLoader}
                    height={OR2_PAGE_HEADER_AVATAR_SIZE}
                    width={OR2_PAGE_HEADER_AVATAR_SIZE}
                  />
                ) : (
                  iconNode
                )}
              </Or2Avatar>
            </Grid>
            <Grid>
              <Typography variant="h4" component="h1">
                {loading ? <Skeleton animation="wave" width="250px" /> : title}
              </Typography>
              {!hideSubtitle && subtitle && (
                <Typography variant="subtitle1" color="textSecondary">
                  {loading ? (
                    <Skeleton animation="wave" width="50%" />
                  ) : (
                    subtitle
                  )}
                </Typography>
              )}
            </Grid>
          </Grid>
          <Grid
            container
            justifyContent={'flex-end'}
            alignContent={'flex-start'}
          >
            <Grid container>
              {actions?.map((action, i) => (
                <Fragment key={i}>{action}</Fragment>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Or2Container>
    </div>
  );
};
